---
title: Waveform
description: "Draws audio: a seekable waveform with regions and markers, a live waveform that scrolls with the signal, and a bar visualizer."
component: true
base: base
---

<ComponentPreview styleName="base-nova" name="waveform-demo" />

Components for drawing audio. `Waveform` shows a whole file and lets you scrub through it. `LiveWaveform` follows a signal as it plays. `BarVisualizer` turns that signal into a few bars you style in CSS. All three wrap the `Waveform` primitive from `@audio-ui/react`, which works with any audio: decoded PCM samples, a microphone or a stream from your server.

## Overview

- **Two kinds of data:** a file's shape is known in advance, so `Waveform` takes `values` (amplitudes from `0` to `1`, at any resolution). A live signal is not, so `LiveWaveform` and `BarVisualizer` take a [`source`](#live-signals) and follow it outside React.
- **Value:** `value` is the playback position, from `0` to `duration` (default `1`, a fraction; pass the track length in seconds to work in seconds). The primitive owns the seek position, not the audio: connect `onValueChange` to your own `<audio>` element.
- **Asymmetric shape:** give `values` the lowest and highest sample of each slice (`{ min, max }`) and the waveform is drawn asymmetric, as in a DAW. Plain amplitudes draw the same shape above and below the middle.
- **Part of the audio:** `range` shows only a stretch of the audio, such as a clip's window onto its source or a zoomed view. The playhead, seeking, hover, regions and markers follow that stretch. See [Showing part of the audio](#showing-part-of-the-audio).
- **Resolution:** the canvas draws as many bars as fit its width and keeps the loudest sample of each bar's slice, so detail holds at any size. Bars align to physical pixels, so their edges stay sharp at any pixel density or zoom. See [Feeding real audio](#feeding-real-audio).
- **Shapes:** `shape="mirror"` (the default) centers the bars, `"bars"` stands them on the baseline, and `"line"` joins the levels into one shape. A live waveform can also be a `"scope"`: raw samples traced as one line, like an oscilloscope.
- **On the timeline:** [`WaveformRegion`](#regions-and-markers) is a stretch you can move and resize, such as a loop. `WaveformMarker` is a labelled point. `WaveformHover` follows the pointer and shows the time under it.
- **Performance:** moving parts are painted outside React, and only when something changes. A waveform that is off screen or at rest uses no frames. When reduced motion is requested, live waveforms repaint a few times per second, without smoothing or idle animation.
- **Accessibility:** the seekable waveform holds a native `<input type="range">`: it takes focus, carries `aria-label`, announces the played share (or the text from `getAriaValueText`, such as a time), and supports the arrow keys, <kbd>Home</kbd>, <kbd>End</kbd>, <kbd>Page Up</kbd> and <kbd>Page Down</kbd>. Each region handle holds one too. Live waveforms are images: give them an `aria-label`.

## Installation

<CodeTabs>

<TabsList>
  <TabsTrigger value="cli"><Icons.shadcn className="size-3.5" /> CLI</TabsTrigger>
  <TabsTrigger value="manual"><Icons.react className="size-3.5" /> Manual</TabsTrigger>
  <TabsTrigger value="agent"><Icons.sparkle className="size-3.5" /> Agent</TabsTrigger>
</TabsList>

<TabsContent value="cli">

```bash component
npx shadcn@latest add @audio-ui/waveform
```

It installs the element alone. For the player above, ready to run: `npx shadcn@latest add @audio-ui/waveform-demo`.

</TabsContent>

<TabsContent value="manual">

<Steps>

<Step>Install the following dependencies:</Step>

```bash
npm install @audio-ui/react
```

<Step>Copy and paste the following code into your project.</Step>

<ComponentSource
  styleName="base-nova"
  name="waveform"
  title="components/audio-ui/waveform.tsx"
/>

<Step>Update the import paths to match your project setup.</Step>

</Steps>

</TabsContent>

<TabsContent value="agent">

<InstallAgent name="waveform" />

</TabsContent>

</CodeTabs>

## Anatomy

Import the primitive and assemble its parts.

```tsx
import { Waveform } from "@audio-ui/react";

<Waveform.Root>
  <Waveform.Control>
    <Waveform.Canvas />
    <Waveform.Playhead />
    <Waveform.Region>
      <Waveform.RegionHandle edge="start" />
      <Waveform.RegionHandle edge="end" />
    </Waveform.Region>
    <Waveform.Marker />
    <Waveform.Hover />
    <Waveform.Thumb />
  </Waveform.Control>
</Waveform.Root>
```

## Usage

```tsx
import {
  BarVisualizer,
  LiveWaveform,
  Waveform,
  WaveformHover,
  WaveformMarker,
  WaveformRegion,
  WaveformRegionHandle,
} from "@/components/audio-ui/waveform";
```

**A file, in seconds, with a playhead line and handle:**

```tsx
<Waveform
  aria-label="Track progress"
  duration={audio.duration}
  onValueChange={(seconds) => {
    audio.currentTime = seconds;
  }}
  value={currentTime}
  values={values}
  variant="playhead"
/>
```

**A microphone, scrolling as it comes in:**

```tsx
const { level } = useAudioSignal(analyser);

<LiveWaveform aria-label="Microphone input" mode="scrolling" source={level} />;
```

**A voice, as bars:**

```tsx
const { bands } = useAudioSignal(analyser);

<BarVisualizer aria-label="Agent speaking" mirrored source={bands} />;
```

## Feeding real audio

`values` is a plain prop: the component does not decode audio. `@audio-ui/core/analysis` and the `audio-decode` lib (`npx shadcn@latest add @audio-ui/audio-decode`) turn a real audio file into `values`: `decodeAudioChannels` (from `audio-decode`) reads every channel's PCM samples out of an `AudioContext`, `computePeaks` reduces them to each slice's lowest and highest sample across all channels (so a take panned hard to one side still draws), and `normalizePeaks` scales them so the loudest sample reaches full height.

```tsx
import { computePeaks, normalizePeaks } from "@audio-ui/core/analysis";
import { decodeAudioChannels } from "@/lib/audio-decode";

const context = new AudioContext();
const channels = await decodeAudioChannels(context, await file.arrayBuffer());

const values = normalizePeaks(computePeaks(channels, 4096));
```

The second argument to `computePeaks` is a resolution, not a bar count: the canvas fits whatever you pass to its own width and to any `range`. Compute it once per file, with more slices than the widest view you expect will show (a few thousand covers a zoomed-in clip editor). For a simpler shape, `downsampleWaveform` and `normalizeWaveform` give one amplitude per slice instead.

## Showing part of the audio

`range` is the stretch of the audio to show, `[start, end]` in `duration` units. Everything follows it: the bars are drawn from that stretch of `values` alone, a press seeks inside it, the input's keys stay inside it, and regions and markers are placed across it.

```tsx
<Waveform
  aria-label="Clip"
  duration={source.duration}
  range={[clip.offset, clip.offset + clip.length]}
  values={source.peaks}
/>
```

When the position is outside the stretch, the root carries `data-outside` and the playhead is hidden, rather than pinned to an edge it hasn't reached. Past the largest canvas a browser will draw (16,384 pixels a side), the bitmap is drawn coarser rather than going blank.

## Following playback

`value` re-renders React each time it changes, which is fine for a `timeupdate` event a few times a second. For a playhead that glides every animation frame, pass a `source`, an object in the shape of an external store:

```ts
interface WaveformSource {
  getSnapshot: () => number; // the position, in `duration` units
  subscribe: (listener: () => void) => () => void;
}
```

The waveform subscribes to it and moves the playhead, repaints the canvas and updates the input itself. While the pointer scrubs, the waveform follows the pointer and ignores the source until release; the same holds for a controlled `value` that updates late.

## Regions and markers

Anything inside `Waveform` is laid over the canvas, on the same timeline. A region is `[start, end]` in `duration` units; drag its body to move it, and give it a handle per edge to resize it:

```tsx
const [loop, setLoop] = useState<readonly [number, number]>([48, 96]);

<Waveform aria-label="Track progress" duration={180} values={values}>
  <WaveformRegion onValueChange={setLoop} value={loop}>
    <WaveformRegionHandle aria-label="Loop start" edge="start" />
    <WaveformRegionHandle aria-label="Loop end" edge="end" />
  </WaveformRegion>
  <WaveformMarker value={124}>Chorus</WaveformMarker>
  <WaveformHover format={formatTime} />
</Waveform>
```

A press on a region never seeks the waveform underneath. Each handle is a slider of its own: it takes focus, and the arrow keys, <kbd>Page Up</kbd> / <kbd>Page Down</kbd>, <kbd>Home</kbd> and <kbd>End</kbd> move its edge, which stops `minLength` short of the other one. Leave the handles out for a region that only moves, or pass `movable={false}` for one that only resizes.

## Live signals

A live waveform reads from a `source`, an object in the shape of an external store whose snapshot is the current levels, `0–1` each:

```ts
interface LevelsSource {
  getSnapshot: () => ArrayLike<number>;
  subscribe: (listener: () => void) => () => void;
}
```

The `use-audio-signal` hook (`npx shadcn@latest add @audio-ui/use-audio-signal`) reads two of them from an `AnalyserNode`, every animation frame and only while something is drawing them:

```tsx
const { level, bands, samples } = useAudioSignal(analyser, { bandCount: 32 });
```

- `level` is the loudest sample of the moment, as a single level. Draw it with `mode="scrolling"`: the waveform records it over time, the newest entering from the right, or from the middle outwards with `mirrored`. When the signal stops (pass `null` as the analyser to pause), the trace holds still.
- `bands` is the spectrum as log-spaced frequency bands, low to high. Draw it with a `BarVisualizer`, or a `LiveWaveform` in its default `mode="static"`: the bars stay in place and follow the bands, rising at once and falling smoothly.
- `samples` is the raw waveform of the moment, `-1` to `1`. Draw it with `shape="scope"`.

Any signal works. `createSource` from `@audio-ui/react` makes a source you push to yourself, from a WebSocket, a worklet or a test:

```tsx
import { createSource } from "@audio-ui/react";

const levels = createSource(new Float32Array(16));

socket.onmessage = (event) => levels.set(event.data);
```

While its signal is audible, a live waveform carries `data-active`. `idle` picks what the bars do meanwhile (`"pulse"` breathes, `"wave"` ripples), and `loading` sweeps across them while a connection is on its way.

## Theming

Colors come from CSS, and the canvas repaints when the theme changes:

<TypeTable title="CSS variables">
<TypeRow name={"--waveform-color"} default={"text color"}>Bars ahead of the position, and every bar of a live waveform.</TypeRow>
<TypeRow name={"--waveform-progress-color"} default={"--waveform-color"}>Bars behind the position.</TypeRow>
<TypeRow name={"--waveform-bar-radius"} default={"0px"}>Bar corner radius on the canvas (any CSS length).</TypeRow>
<TypeRow name={"--waveform-position"}>Set on the root: the playback position, as a percentage.</TypeRow>
<TypeRow name={"--waveform-level"}>Set on each bar of a `BarVisualizer`: its level, `0–1`.</TypeRow>
</TypeTable>

The bars of a `BarVisualizer` are plain elements: restyle them with `.cn-bar-visualizer-bar`. Those the playback position has passed carry `data-played`.

## API reference

### Root

Groups the parts of a waveform and owns what they show: the audio's shape (`peaks`, known upfront, or a live `levels` signal) and, for a seekable one, the playback position in `duration` units. The position comes from `value`, or from a live `source` that the waveform follows straight in the DOM without re-rendering React; it is exposed as the `--waveform-position` CSS variable (a percentage). While a live signal is audible the element carries `data-active`. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"defaultValue"} type={"number"} default={"0"}>Uncontrolled starting position.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"}></TypeRow>
<TypeRow name={"duration"} type={"number"} default={"1"}>Length of the audio, in the unit `value` is in (typically seconds).</TypeRow>
<TypeRow name={"largeStep"} type={"number"}>Step for Page Up/Page Down and Shift + arrow keys. Defaults to a tenth of `duration`.</TypeRow>
<TypeRow name={"levels"} type={"Levels"}>A live signal drawn outside React, instead of `peaks`: a microphone, an analyser, a stream.</TypeRow>
<TypeRow name={"loading"} type={"boolean"} default={"false"}>Whether the audio is still on its way. Seeking waits, and the bars sweep.</TypeRow>
<TypeRow name={"onValueChange"} type={"(value: number, eventDetails: ChangeEventDetails) => void"}>Fires while seeking: press, drag, keyboard and input changes.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value: number, eventDetails: CommitEventDetails) => void"}>Fires when a seek that moved the position ends.</TypeRow>
<TypeRow name={"peaks"} type={"WaveformPeaks"}>The audio's shape, at any resolution: a level per slice (0…1), or the lowest and highest sample of each (`{ min, max }`, -1…1), which draws the waveform lopsided where the audio is. The parts fit it to their size.</TypeRow>
<TypeRow name={"range"} type={"readonly [number, number]"}>The stretch of the audio to show, `[start, end]` in `duration` units: a clip's window onto its source, a zoomed view. The playhead, seeking, hover, regions and markers all follow it. Defaults to the whole audio. While the position is outside it, the root carries `data-outside`.</TypeRow>
<TypeRow name={"source"} type={"Source"}>A live position followed outside React; takes precedence over `value`.</TypeRow>
<TypeRow name={"step"} type={"number"}>Step for the arrow keys. Defaults to a hundredth of `duration`.</TypeRow>
<TypeRow name={"value"} type={"number"}>Playback position, from 0 to `duration`.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Region

A stretch of the timeline, such as a loop or a selection, laid over the waveform from `value[0]` to `value[1]`. Dragging its body moves it; add a `Waveform.RegionHandle` per edge to resize it. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"defaultValue"} type={"Value"}>Uncontrolled starting region. Defaults to the whole timeline.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"}></TypeRow>
<TypeRow name={"minLength"} type={"number"}>The shortest the region can be resized to, in `duration` units. Defaults to the root's `step`.</TypeRow>
<TypeRow name={"movable"} type={"boolean"} default={"true"}>Whether dragging the region's body moves it.</TypeRow>
<TypeRow name={"onValueChange"} type={"(value: Value, eventDetails: ChangeEventDetails) => void"}>Fires while the region is moved or resized.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value: Value, eventDetails: CommitEventDetails) => void"}>Fires when a move or a resize that changed the region ends.</TypeRow>
<TypeRow name={"value"} type={"Value"}>The region, as `[start, end]` in `duration` units.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is moving or resizing the region.</TypeRow>
</TypeTable>

### RegionHandle

Resizes one edge of the region: by dragging, or from the keyboard through the native `<input type="range">` it holds, which carries the accessible name (`aria-label` / `aria-labelledby`). Renders a `<div>` element.

<TypeTable>
<TypeRow name={"edge"} type={"\"end\" | \"start\""}>The edge of the region this handle resizes.</TypeRow>
<TypeRow name={"getAriaValueText"} type={"DescribeValue"}>Returns how the position is announced, e.g. `"0:12 of 1:42"`. Defaults to the played share, e.g. `"30%"`.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is moving or resizing the region.</TypeRow>
</TypeTable>

### Control

The seekable surface: a press jumps to the pointer and a drag scrubs. It tracks the hovered position for `Waveform.Hover`, sets `touch-action: none` so touch scrubbing doesn't scroll the page, and holds the native `<input type="range">` that takes focus, handles the keyboard and carries the accessible name (`aria-label` / `aria-labelledby`). Leave it out for a waveform that is only shown. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"getAriaValueText"} type={"DescribeValue"}>Returns how the position is announced, e.g. `"0:12 of 1:42"`. Defaults to the played share, e.g. `"30%"`.</TypeRow>
<TypeRow name={"onSelectionChange"} type={"(range: readonly [number, number], eventDetails) => void"}>Fires while a stretch is selected by dragging with Shift held, as `[start, end]` in `duration` units; feed it to a `Waveform.Region`. A plain press and drag still seek. `eventDetails.cancel()` keeps the selection as it was.</TypeRow>
<TypeRow name={"onSelectionCommitted"} type={"(range: readonly [number, number], eventDetails) => void"}>Fires when a selection that changed ends.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Bars

Draws the waveform as a fixed number of elements, one `Waveform.Bar` each, for a visualizer styled in plain CSS. Each bar's level, 0…1, is written to its `--waveform-level` CSS variable outside React: levels rise at once and fall smoothly, and nothing is written while they rest or sit off screen. Bars the playback position has passed carry `data-played`. Its children are a function called once per bar; left out, plain bars are rendered. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"children"} type={"(index: number, count: number) => React.ReactNode"}>Called once per bar; returns its `Waveform.Bar`.</TypeRow>
<TypeRow name={"count"} type={"number"} default={"24"}>How many bars to draw. The levels are fitted to them.</TypeRow>
<TypeRow name={"gain"} type={"number"} default={"1"}>Visual gain applied to the levels before they are drawn.</TypeRow>
<TypeRow name={"idle"} type={"WaveformIdle"} default={"\"none\""}>What the bars do while there is no signal.</TypeRow>
<TypeRow name={"minLevel"} type={"number"} default={"0.08"}>Resting level, 0…1: silent bars keep this much of their size.</TypeRow>
<TypeRow name={"mirrored"} type={"boolean"} default={"false"}>Mirrors the levels around the middle bar, the first one in the center.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Bar

One bar of `Waveform.Bars`. Its level, 0…1, is its `--waveform-level` CSS variable: size it with `height: calc(var(--waveform-level) * 100%)`, or the width for a vertical layout. Renders a `<span>` element.

<TypeTable>
<TypeRow name={"index"} type={"number"}>The bar's place in the row, from 0.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Canvas

Draws the waveform on a canvas, laid out on the device pixel grid: as many bars as fit its width, every edge on a physical pixel, so it stays sharp at any pixel density and zoom level. It paints only when something changed, and not at all while it is off screen. With `peaks`, the played part is drawn in one color and the rest in another. With a live `levels` signal, `mode` picks between the current levels across the width and a scrolling record of the loudest one. The `scope` variant takes raw samples instead of levels, and traces them as they are. Colors come from CSS: `--waveform-color` and `--waveform-progress-color` (both default to the text color). The bar corner radius is `barRadius`, or the `--waveform-bar-radius` CSS length. Renders a `<canvas>` element.

<TypeTable>
<TypeRow name={"barGap"} type={"number"} default={"1"}>Space between bars, in CSS pixels, snapped to whole device pixels.</TypeRow>
<TypeRow name={"barRadius"} type={"number"}>Bar corner radius, in CSS pixels. Defaults to the `--waveform-bar-radius` CSS length.</TypeRow>
<TypeRow name={"barWidth"} type={"number"} default={"2"}>Bar width, in CSS pixels, snapped to whole device pixels.</TypeRow>
<TypeRow name={"gain"} type={"number"} default={"1"}>Visual gain applied to the levels before they are drawn.</TypeRow>
<TypeRow name={"idle"} type={"WaveformIdle"} default={"\"none\""}>What the bars do while there is no signal, outside the scrolling mode.</TypeRow>
<TypeRow name={"lineWidth"} type={"number"} default={"1.5"}>Thickness of the `scope` trace, in CSS pixels.</TypeRow>
<TypeRow name={"minBarHeight"} type={"number"} default={"2"}>Shortest bar, in CSS pixels: silence still draws a sliver, so the timeline stays readable.</TypeRow>
<TypeRow name={"mirrored"} type={"boolean"} default={"false"}>Makes the drawing symmetric around the middle: the first level sits in the center, or, when scrolling, the newest one, with older ones moving out on both sides.</TypeRow>
<TypeRow name={"mode"} type={"\"scrolling\" | \"static\""} default={"\"static\""}>How a live `levels` signal is drawn: `static` spreads the current levels across the width; `scrolling` records the loudest one over time, the newest entering from the right.</TypeRow>
<TypeRow name={"scrollSpeed"} type={"number"} default={"60"}>How fast the scrolling mode moves, in CSS pixels per second.</TypeRow>
<TypeRow name={"variant"} type={"WaveformVariant"} default={"\"mirror\""}>`mirror` centers the bars on the middle line, `bars` stands them on the baseline, and `line` joins the levels into one continuous, centered shape. `scope` traces raw samples, -1…1, as a single line.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Thumb

A handle centered on the playback position. Renders a `<div>` element.

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Marker

A point of interest on the timeline, such as a cue or a chapter, positioned at `value`. Its children label it. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"value"} type={"number"}>Where the marker sits, from 0 to `duration`.</TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Playhead

A marker at the playback position, with no interaction of its own. Renders a `<div>` element.

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Hover

Shown while the pointer is over the `Control`, positioned at the hovered point. Its children receive the hovered position, to print a time. Renders a `<div>` element.

<TypeTable>
<TypeRow name={"children"} type={"React.ReactNode | ((value: number) => React.ReactNode)"}></TypeRow>
</TypeTable>

<TypeTable title="Data attributes">
<TypeRow name={"data-disabled"}>Present when disabled.</TypeRow>
<TypeRow name={"data-dragging"}>Whether a pointer is scrubbing the waveform.</TypeRow>
<TypeRow name={"data-loading"}>Whether the audio is still on its way: there is nothing to seek yet.</TypeRow>
</TypeTable>

### Registry component

`aria-label`, `aria-labelledby`, and `aria-describedby` are forwarded to the element that needs them: the waveform's input, a region handle's input, or the live waveform itself.

#### Waveform

<TypeTable>
<TypeRow name={"values"} type={"ArrayLike<number> | { min, max }"}>The audio's shape at any resolution: `0–1` amplitudes, or each slice's lowest and highest sample, `-1–1`.</TypeRow>
<TypeRow name={"range"} type={"readonly [number, number]"} default={"whole audio"}>The stretch of the audio to show, `[start, end]` in `duration` units: a clip's window onto its source, a zoomed view. The playhead, seeking, hover, regions and markers all follow it. Defaults to the whole audio. While the position is outside it, the root carries `data-outside`.</TypeRow>
<TypeRow name={"value"} type={"number"}>Controlled position, from `0` to `duration`.</TypeRow>
<TypeRow name={"defaultValue"} type={"number"} default={"0"}>Uncontrolled starting position.</TypeRow>
<TypeRow name={"source"} type={"WaveformSource"}>A live position followed outside React; takes precedence over `value`.</TypeRow>
<TypeRow name={"duration"} type={"number"} default={"1"}>Length of the audio, in the unit `value` is in (typically seconds).</TypeRow>
<TypeRow name={"step"} type={"number"} default={"duration / 100"}>Step for the arrow keys. Defaults to a hundredth of `duration`.</TypeRow>
<TypeRow name={"largeStep"} type={"number"} default={"duration / 10"}>Step for Page Up/Page Down and Shift + arrow keys. Defaults to a tenth of `duration`.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"} default={"false"}>Disables pointer and keyboard interaction.</TypeRow>
<TypeRow name={"loading"} type={"boolean"} default={"false"}>Whether the audio is still on its way. Seeking waits, and the bars sweep.</TypeRow>
<TypeRow name={"onValueChange"} type={"(value: number, eventDetails) => void"}>Fires while seeking. `eventDetails.reason` is `"press"`, `"drag"`, `"keyboard"`, or `"input-change"`; call `eventDetails.cancel()` to reject it.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value: number, eventDetails) => void"}>Fires when a seek that moved the position ends.</TypeRow>
<TypeRow name={"getAriaValueText"} type={"(value: number, duration: number) => string"} default={"played share"}>Returns how the position is announced, e.g. `"0:12 of 1:42"`. Defaults to the played share, e.g. `"30%"`.</TypeRow>
<TypeRow name={"onSelectionChange"} type={"(range: readonly [number, number], eventDetails) => void"}>Fires while a stretch is selected by dragging with Shift held, as `[start, end]` in `duration` units; feed it to a `Waveform.Region`. A plain press and drag still seek. `eventDetails.cancel()` keeps the selection as it was.</TypeRow>
<TypeRow name={"onSelectionCommitted"} type={"(range: readonly [number, number], eventDetails) => void"}>Fires when a selection that changed ends.</TypeRow>
<TypeRow name={"shape"} type={"\"mirror\" | \"bars\" | \"line\""} default={"\"mirror\""}>How the amplitudes are drawn.</TypeRow>
<TypeRow name={"variant"} type={"\"default\" | \"playhead\""} default={"\"default\""}>`playhead` adds a playhead line and a handle.</TypeRow>
<TypeRow name={"className"} type={"string"}>Classes for the waveform surface.</TypeRow>
</TypeTable>

#### WaveformRegion

<TypeTable>
<TypeRow name={"value"} type={"readonly [number, number]"}>Controlled region, as `[start, end]` in `duration` units.</TypeRow>
<TypeRow name={"defaultValue"} type={"readonly [number, number]"} default={"whole timeline"}>Uncontrolled starting region.</TypeRow>
<TypeRow name={"minLength"} type={"number"} default={"the waveform's step"}>The shortest the region can be resized to, in `duration` units. Defaults to the root's `step`.</TypeRow>
<TypeRow name={"movable"} type={"boolean"} default={"true"}>Whether dragging the region's body moves it.</TypeRow>
<TypeRow name={"disabled"} type={"boolean"} default={"false"}>Also disabled when the waveform is.</TypeRow>
<TypeRow name={"onValueChange"} type={"(value, eventDetails) => void"}>Fires while moving or resizing. `eventDetails.edge` is `"start"`, `"end"` or `"both"`; `eventDetails.reason` is `"drag"`, `"keyboard"` or `"input-change"`.</TypeRow>
<TypeRow name={"onValueCommitted"} type={"(value, eventDetails) => void"}>Fires when a move or resize that changed the region ends.</TypeRow>
</TypeTable>

`WaveformRegionHandle` takes `edge` (`"start"` or `"end"`) and `getAriaValueText`. `WaveformMarker` takes `value` and shows its children as a label. `WaveformHover` takes `format`, which prints the hovered position; without it only the line shows.

#### LiveWaveform

<TypeTable>
<TypeRow name={"source"} type={"LevelsSource | null"}>A live position followed outside React; takes precedence over `value`.</TypeRow>
<TypeRow name={"mode"} type={"\"static\" | \"scrolling\""} default={"\"static\""}>`static` spreads the current levels across the width; `scrolling` records the loudest one over time.</TypeRow>
<TypeRow name={"scrollSpeed"} type={"number"} default={"60"}>How fast the scrolling mode moves, in CSS pixels per second.</TypeRow>
<TypeRow name={"shape"} type={"\"mirror\" | \"bars\" | \"line\" | \"scope\""} default={"\"mirror\""}>How the signal is drawn. `scope` traces raw samples instead of levels.</TypeRow>
<TypeRow name={"gain"} type={"number"} default={"1"}>Visual gain applied to the levels before they are drawn.</TypeRow>
<TypeRow name={"mirrored"} type={"boolean"} default={"false"}>Makes the drawing symmetric: the first level in the center, or, when scrolling, the newest one, with older ones moving out on both sides.</TypeRow>
<TypeRow name={"idle"} type={"\"none\" | \"pulse\" | \"wave\""} default={"\"none\""}>In `static` mode, what the bars do while there is no signal.</TypeRow>
<TypeRow name={"loading"} type={"boolean"} default={"false"}>Whether the audio is still on its way. Seeking waits, and the bars sweep.</TypeRow>
</TypeTable>

#### BarVisualizer

<TypeTable>
<TypeRow name={"source"} type={"LevelsSource | null"}>A live position followed outside React; takes precedence over `value`.</TypeRow>
<TypeRow name={"count"} type={"number"} default={"24"}>How many bars to draw. The levels are fitted to them.</TypeRow>
<TypeRow name={"mirrored"} type={"boolean"} default={"false"}>Mirrors the levels around the middle bar, the lowest band in the center.</TypeRow>
<TypeRow name={"minLevel"} type={"number"} default={"0.08"}>Resting level, 0…1: silent bars keep this much of their size.</TypeRow>
<TypeRow name={"gain"} type={"number"} default={"1"}>Visual gain applied to the levels before they are drawn.</TypeRow>
<TypeRow name={"idle"} type={"\"none\" | \"pulse\" | \"wave\""} default={"\"none\""}>What the bars do while there is no signal.</TypeRow>
<TypeRow name={"loading"} type={"boolean"} default={"false"}>Whether the audio is still on its way. Seeking waits, and the bars sweep.</TypeRow>
<TypeRow name={"align"} type={"\"start\" | \"center\" | \"end\""} default={"\"center\""}>Where the bars grow from.</TypeRow>
<TypeRow name={"orientation"} type={"\"horizontal\" | \"vertical\""} default={"\"horizontal\""}>`vertical` stacks the bars and grows them sideways.</TypeRow>
</TypeTable>



`@audio-ui/react/waveform` exposes the parts these components are built from:

<TypeTable title="Parts">
<TypeRow name={"Waveform.Root"}>Owns the data (`peaks`, or a live `levels` source) and the position (`value`, `source`, `duration`). Carries `data-active` while a live signal is audible.</TypeRow>
<TypeRow name={"Waveform.Control"}>The seekable surface; holds the input. Leave it out for a waveform that is only shown.</TypeRow>
<TypeRow name={"Waveform.Canvas"}>Draws on a canvas: `variant`, `mode`, `scrollSpeed`, `barWidth`, `barGap`, `barRadius`, `minBarHeight`, `lineWidth`, `gain`, `mirrored`, `idle`.</TypeRow>
<TypeRow name={"Waveform.Bars, Waveform.Bar"}>Draws as elements: `count`, `minLevel`, `gain`, `mirrored`, `idle`. Each bar gets `--waveform-level`, `data-index` and `data-played`.</TypeRow>
<TypeRow name={"Waveform.Region, Waveform.RegionHandle"}>A stretch of the timeline, and the handle of one of its edges.</TypeRow>
<TypeRow name={"Waveform.Marker"}>A point on the timeline.</TypeRow>
<TypeRow name={"Waveform.Playhead, Waveform.Thumb"}>Follow the playback position.</TypeRow>
<TypeRow name={"Waveform.Hover"}>Shown at the hovered position; its children receive that position.</TypeRow>
</TypeTable>

Every part accepts a `render` prop and `className` / `style` as functions of state, and exposes that state as data attributes such as `data-disabled`, `data-dragging` and `data-loading`.

## Examples

### Default

<ComponentPreview styleName="base-nova" name="waveform-demo" />

### Region, markers and hover time

<ComponentPreview styleName="base-nova" name="waveform-region-demo" />

### Live

The signal is recorded as it comes in, the newest level entering from the right.

<ComponentPreview styleName="base-nova" name="waveform-live-demo" />

### Player, wired to a real uploaded file

<ComponentPreview styleName="base-nova" name="block-waveform-player" />
